import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useLocations } from '@/hooks/use-locations';

type LocationPickerBaseProps = {
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Location IDs to hide from the options (e.g. already-assigned). */
  excludeIds?: string[];
  /**
   * Custom popover trigger. When provided it replaces the default input-style
   * button — e.g. a compact "+ Add" button in a section header. Must forward
   * ref + props (a shadcn Button does).
   */
  trigger?: React.ReactNode;
};

type SingleProps = LocationPickerBaseProps & {
  multiple?: false;
  selected: string;
  onChange: (id: string) => void;
};

type MultiProps = LocationPickerBaseProps & {
  multiple: true;
  selected: string[];
  onChange: (ids: string[]) => void;
};

export type LocationPickerProps = SingleProps | MultiProps;

export function LocationPicker(props: LocationPickerProps): React.ReactElement {
  const {
    disabled = false,
    placeholder = 'Select location...',
    className,
    excludeIds,
    trigger,
  } = props;
  const [open, setOpen] = useState(false);
  const { data: locationsData } = useLocations();

  const options = useMemo(() => {
    const exclude = new Set(excludeIds ?? []);
    return (locationsData?.items ?? [])
      .filter((l) => l.rowStatus === 0 && !exclude.has(l.id))
      .map((l) => ({ id: l.id, label: l.name }));
  }, [locationsData?.items, excludeIds]);

  const isMulti = props.multiple === true;
  const selectedIds = isMulti ? props.selected : props.selected ? [props.selected] : [];
  const selectedOptions = options.filter((o) => selectedIds.includes(o.id));

  const toggle = (id: string) => {
    if (isMulti) {
      const current = props.selected;
      if (current.includes(id)) {
        props.onChange(current.filter((s) => s !== id));
      } else {
        props.onChange([...current, id]);
      }
    } else {
      props.onChange(id);
      setOpen(false);
    }
  };

  const displayText = isMulti ? null : (selectedOptions[0]?.label ?? null);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {isMulti && selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((opt) => (
            <Badge key={opt.id} variant="secondary" className="gap-1 py-0 text-xs">
              {opt.label}
              {!disabled && (
                <button
                  type="button"
                  className="ml-0.5 rounded-full hover:text-destructive"
                  onClick={() => toggle(opt.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              variant="outline"
              type="button"
              className={cn('w-full justify-between', isMulti ? '' : 'h-9 text-xs font-normal')}
              disabled={disabled}
            >
              {displayText ? (
                <span className="truncate">{displayText}</span>
              ) : (
                <span className="truncate text-muted-foreground">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command
            filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
          >
            <CommandInput placeholder="Search locations..." />
            <CommandList>
              <CommandEmpty>No locations found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.label}
                    onSelect={() => toggle(option.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        selectedIds.includes(option.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
